import type { ReactElement } from 'react';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUIDSeed } from 'react-uid';

import Plan, { PlanType, Programs } from 'models/Plan';
import IPlanInfo from 'models/IPlanInfo';

import useAsyncEffect from 'hooks/useAsyncEffect';
import useSubscription from 'hooks/useSubscription';

import { Route, ApiRoute, FeatureFlag } from 'config';
import { get, post } from 'utils/network';
import { toCurrency } from 'utils/numbers';
import useCurrentUser from 'hooks/useCurrentUser';

import preparedImgUrl from 'consumer/signup/plans/pricing_plan_select/prepared.jpg';
import preparedImgUrl2x from 'consumer/signup/plans/pricing_plan_select/prepared@2x.jpg';
import mealKitImgUrl from 'consumer/signup/plans/pricing_plan_select/meal_kit.jpg';
import mealKitImgUrl2x from 'consumer/signup/plans/pricing_plan_select/meal_kit@2x.jpg';
import programsImgUrl from 'consumer/signup/plans/pricing_plan_select/programs.jpg';
import programsImgUrl2x from 'consumer/signup/plans/pricing_plan_select/programs@2x.jpg';
import mostPopularImgUrl from 'consumer/components/green-arrow-right.svg';
import { Component as RightArrowIcon } from 'icons/icon-ui--arrow-right-outline.svg';

import FAQs from 'components/shared/FAQs';
import PlanRecipes from './PlanRecipes';

const servingOptions = {
  [PlanType.MEAL_KIT]: Plan.getServingOptions(PlanType.MEAL_KIT),
  [PlanType.PREPARED]: Plan.getServingOptions(PlanType.PREPARED),
  [PlanType.PROGRAMS]: Plan.getServingOptions(PlanType.PROGRAMS),
};

const extraDinnerOptions = {
  [PlanType.MEAL_KIT]: Plan.getExtraDinnerOptions(PlanType.MEAL_KIT),
  [PlanType.PREPARED]: Plan.getExtraDinnerOptions(PlanType.PREPARED),
};

const defaultServings = 2;
const defaultMealKitExtras = { dinner: 1, breakfast: 0, lunch: 0 };
const defaultPreparedExtras = { dinner: 2, breakfast: 0, lunch: 0 };
const defaultProgramsExtras = { dinner: 0, breakfast: 0, lunch: 0 };

/* TODO: refactor this page to move away from logic based on # of servings, since programs and RTE
 *  both have 1 serving. We also need to break this into smaller components.
 */
function Plans(): ReactElement {
  const seed = useUIDSeed();
  const navigate = useNavigate();

  const { currentUser } = useCurrentUser();
  const { subscription, reload: reloadSubscription } = useSubscription();

  const signupSummary = subscription.signup_summary;
  const menuDate = signupSummary?.delivery_date;
  const discount = signupSummary?.discount?.value;
  const couponDiscount = signupSummary?.discount?.type === 'coupon_discount';
  const couponCount = signupSummary?.discount?.coupon_count;
  const couponName = signupSummary?.discount?.coupon_name;
  const couponType = signupSummary?.discount?.coupon_discount_type;
  const couponValue = signupSummary?.discount?.coupon_value;

  const isProgramsPlanSelectEnabled = currentUser
    .hasFeatureEnabled(FeatureFlag.PROGRAMS_PLAN_SELECT);

  const [mealKit2SPlan, setMealKit2SPlan] = useState(
    Plan.getPlan(2, defaultMealKitExtras),
  );
  const [mealKit4SPlan, setMealKit4SPlan] = useState(
    Plan.getPlan(4, defaultMealKitExtras),
  );
  const [preparedPlan, setPreparedPlan] = useState(
    Plan.getPlan(1, defaultPreparedExtras),
  );
  const programsPlan = Plan.getPlan(1, defaultProgramsExtras, Programs.JUMPSTART_PROGRAM);
  const [isProgramsSelected, setIsProgramsSelected] = useState(
    subscription?.plan === Programs.JUMPSTART_PROGRAM,
  );

  const planByServings = {
    2: mealKit2SPlan,
    4: mealKit4SPlan,
    1: preparedPlan,
  };

  const planSetterByServings = useMemo(() => ({
    2: setMealKit2SPlan,
    4: setMealKit4SPlan,
    1: setPreparedPlan,
  }), [setMealKit2SPlan, setMealKit4SPlan, setPreparedPlan]);

  const [selectedServings, setSelectedServings] = useState(
    defaultServings,
  );

  const [savedMealKitServings, setSavedMealKitServings] = useState(
    defaultServings,
  );

  const currentPlan = isProgramsSelected ? programsPlan : planByServings[selectedServings];
  const currentPlanType = currentPlan.type;

  const [prices, setPrices] = useState({
    servingPrice: 0,
    planPrice: 0,
    totalPrice: 0,
  });

  const [plansInfo, setPlansInfo] = useState<IPlanInfo[]>([]);
  const [isPlanInitialized, setPlanInitialized] = useState(Plan.isInitialized);

  const handlePlanChange = async (newPlan: Plan, servings: number) => {
    setIsProgramsSelected(newPlan.type === PlanType.PROGRAMS);
    setSelectedServings(servings);

    await post(`${ApiRoute.USERS}/me/subscription`, {
      subscription: {
        plan: newPlan.name,
        extra_meals: newPlan.selectedExtras,
      },
    });

    reloadSubscription();
  };

  const handleServingChange = (servings: number) => {
    if (currentPlanType === PlanType.MEAL_KIT) {
      setSavedMealKitServings(servings);
    }

    handlePlanChange(currentPlan, servings);
  };

  const postPlan = async () => {
    await post(`${ApiRoute.USERS}/me/subscription`, {
      subscription: {
        plan: currentPlan.name,
        extra_meals: currentPlan.selectedExtras,
      },
    });
  };

  const setSelectedExtras = (extraDinners: number) => {
    const newPlan = Plan.getPlan(selectedServings, {
      dinner: extraDinners,
      breakfast: 0,
      lunch: 0,
    });

    planSetterByServings[newPlan.servings](newPlan);
    handlePlanChange(newPlan, selectedServings);
  };

  const setPricesByPlan = useCallback((plan: Plan) => {
    if (isPlanInitialized && discount !== undefined) {
      const planPrice = isProgramsSelected
        ? plan.planPrice
        : plan.servingPrice * plan.servings * (plan.includedDinners + plan.extraDinners);

      const discountAmount = couponType === 'percentage_off'
        ? (couponValue / 100) * planPrice
        : discount;

      setPrices({
        planPrice,
        servingPrice: plan.servingPrice,
        totalPrice: Math.max(
          0,
          planPrice + plan.breakfastAndLunchPrice - discountAmount,
        ),
      });
    }
  }, [discount, isPlanInitialized, couponType, couponValue, isProgramsSelected]);

  useAsyncEffect(async (isMounted) => {
    if (!Plan.isInitialized) {
      const response = await get<IPlanInfo[]>(ApiRoute.PLANS);

      if (response.parsedBody) {
        Plan.initialize(response.parsedBody);
      }

      if (isMounted && response.parsedBody) {
        setPlansInfo(response.parsedBody);
        setPlanInitialized(true);
      }
    }
  }, []);

  useEffect(() => {
    setPricesByPlan(currentPlan);
  }, [setPricesByPlan, currentPlan]);

  useEffect(() => {
    if (!currentUser.isEmpty) {
      const newPlan = new Plan(
        currentUser.subscription.plan,
        currentUser.subscription.extras,
      );

      setSelectedServings(newPlan.servings);

      if (newPlan.type === PlanType.PROGRAMS) {
        setIsProgramsSelected(true);
      } else {
        setIsProgramsSelected(false);
        planSetterByServings[newPlan.servings](newPlan);
      }
    }
  }, [currentUser, planSetterByServings]);

  return (
    <>
      <div className="c-pricing c-pricing--prepared">

        <div className="c-pricing__header">
          <h1 className="c-pricing__title c-heading c-heading--brand-3">
            Plants Your Way
          </h1>
        </div>

        <div className="c-pricing__disclaimer">
          <div className="c-pricing__disclaimer-image d-none" />
          <div className="c-pricing__disclaimer-copy u-text--xl">
            <div>
              Get your custom weekly fix of fresh plant-based meal kits, ready-to-eat meals,
              and grocery items. Skip or cancel anytime.
            </div>
          </div>
        </div>

        <div className="c-plan-select c-plan-select--prepared js-plan-select">

          <h2 className="c-plan-select__step-title c-heading--brand-5">
            1 - What type of plan are you interested in?
          </h2>

          <div className="c-card-container">
            <button
              type="button"
              className={[
                'c-plan-select-card',
                currentPlanType === PlanType.MEAL_KIT ? 'is-selected' : '',
              ].join(' ')}
              onClick={() => {
                handlePlanChange(planByServings[savedMealKitServings], savedMealKitServings);
              }}
            >
              <img
                src={mealKitImgUrl}
                srcSet={`${mealKitImgUrl} 1x, ${mealKitImgUrl2x} 2x`}
                className="c-plan-select__image"
                alt=""
              />
              <h2 className="c-plan-select__title c-heading--brand-5 c-heading--no-margin">Meal Kit</h2>
            </button>
            <button
              type="button"
              className={[
                'c-plan-select-card',
                currentPlanType === PlanType.PREPARED ? 'is-selected' : '',
              ].join(' ')}
              onClick={() => handlePlanChange(planByServings[1], 1)}
            >
              <img
                src={preparedImgUrl}
                srcSet={`${preparedImgUrl} 1x, ${preparedImgUrl2x} 2x`}
                className="c-plan-select__image"
                alt=""
              />
              <h2 className="c-plan-select__title c-heading--brand-5 c-heading--no-margin">Ready-to-Eat</h2>
            </button>
            {isProgramsPlanSelectEnabled && (
              <button
                type="button"
                className={[
                  'c-plan-select-card',
                  isProgramsSelected ? 'is-selected' : '',
                ].join(' ')}
                onClick={() => handlePlanChange(programsPlan, 1)}
              >
                <img
                  src={programsImgUrl}
                  srcSet={`${programsImgUrl} 1x, ${programsImgUrl2x} 2x`}
                  className="c-plan-select__image c-plan-select__programs-image"
                  alt=""
                />
                <h2 className="c-plan-select__title c-heading--brand-5 c-heading--no-margin">Plant-Based Jumpstart</h2>
              </button>
            )}
          </div>

          <div className="c-plan-select__step-two-container">
            <h2 className="c-plan-select__step-title c-heading--brand-5">
              { isProgramsSelected ? '2 - Meals Curated for You' : '2 - How much food do you want in a week?' }
            </h2>

            <div
              className="c-plan-select__options is-selected"
              data-plan-type="meal_kit"
            >
              <fieldset>
                <div className="c-plan-select__servings">
                  <legend id="plan-servings">
                    Number of servings per meal
                  </legend>
                  <ul
                    role="radiogroup"
                    className="c-switch"
                    aria-labelledby="plan-servings"
                  >
                    {servingOptions[currentPlanType].map((servings) => (
                      <li key={seed(servings)}>
                        <button
                          type="button"
                          role="radio"
                          aria-label={`Select ${servings} Serving`}
                          aria-checked={currentPlan.servings === servings}
                          className={currentPlan.servings === servings
                            ? 'is-selected'
                            : ''}
                          onClick={() => handleServingChange(servings)}
                        >
                          {servings}
                          <span className="sr-only">serving</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              </fieldset>

              <fieldset>
                <div className="c-plan-select__dinners">
                  <legend id="plan-servings">
                    Meals per week
                  </legend>
                  {isProgramsSelected ? (
                    <div className="c-plan-select__program-options">
                      <strong>4 </strong>
                      Ready-to-Eat Breakfasts,
                      {' '}
                      <strong>4 </strong>
                      Lunches,
                      {' '}
                      <strong>4 </strong>
                      Dinners
                    </div>
                  ) : (
                    <ul
                      role="radiogroup"
                      className="c-switch"
                      aria-labelledby="plan-servings"
                    >
                      {extraDinnerOptions[currentPlanType].map((extraDinners) => (
                        <li key={seed(extraDinners)}>
                          <button
                            type="button"
                            role="radio"
                            aria-label={
                                    `Select ${
                                      currentPlan.includedDinners + extraDinners
                                    } Meals`
                                  }
                            aria-checked={
                                      currentPlan.extraDinners === extraDinners
                                  }
                            className={
                                    currentPlan.extraDinners === extraDinners
                                      ? 'is-selected'
                                      : ''
                                  }
                            onClick={() => setSelectedExtras(extraDinners)}
                          >
                            {currentPlan.includedDinners + extraDinners}
                            <span className="sr-only">serving</span>
                          </button>
                          {extraDinners === currentPlan.defaultExtraDinners && (
                          <div className="c-plan-select__most-popular-widget">
                            <img
                              src={mostPopularImgUrl}
                              className="c-plan-select__most-popular-arrow"
                              alt="green arrow right"
                            />
                            <div className="c-plan-select__most-popular-text">
                              Most Popular
                            </div>
                          </div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </fieldset>

              <div className="c-plan-select__options is-selected">
                <div className="c-plan-select__details">
                  <div className="c-plan-select__price-details">
                    <div>Cost per serving</div>
                    <div>
                      {!!prices.servingPrice && toCurrency(prices.servingPrice)}
                    </div>
                  </div>
                  <div className="c-plan-select__price-details">
                    <div>Weekly price</div>
                    {discount > 0
                      ? <s>{!!prices.planPrice && toCurrency(prices.planPrice)}</s>
                      : <div>{!!prices.planPrice && toCurrency(prices.planPrice)}</div>}
                  </div>
                  {couponDiscount && !!discount && (
                  <div className="c-plan-select__price-details">
                    <div>
                      Coupon (
                      {couponName}
                      )
                    </div>
                    <div>
                      {couponType === 'percentage_off'
                        ? `${couponValue}%`
                        : toCurrency(discount)}
                    </div>
                  </div>
                  )}
                  <div className="c-plan-select__price-details">
                    <div>
                      <strong>
                        {discount > 0 && (
                          couponCount > 1
                            ? `First ${couponCount} boxes (with discount)`
                            : 'First week (with discount)'
                        )}
                        {!discount && 'First week'}
                      </strong>
                    </div>
                    <div>
                      <strong>
                        {!!prices.totalPrice && toCurrency(prices.totalPrice)}
                      </strong>
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  title="Select chefs_choice"
                  className={[
                    'c-plan__button',
                    'c-button',
                    'c-button--primary',
                    'c-button--with-icon',
                  ].join(' ')}
                  onClick={async () => {
                    await postPlan();
                    navigate(Route.SIGNUP_CHECKOUT_ADDRESS);
                  }}
                >
                  <RightArrowIcon viewBox="0 0 12 20" />
                  Select This Plan
                </button>
              </div>
            </div>
          </div>
        </div>

      </div>

      <PlanRecipes
        skus={plansInfo.find(({ name }) => name === currentPlan.name)?.skus || []}
          // @ts-expect-error TODO: fix null safety
        menuDate={menuDate && new Date(menuDate.replace(/-/g, '/'))}
      />
      <br />

      <FAQs
        questions={
            [{
              question: 'Can I choose my meals?',
              answer: 'Absolutely! You can pick from any of the meal kits or '
              + 'prepared meals on the menu. And don’t forget to drop some of '
              + 'our hand-picked, plant-based grocery items in your box, too!',
            },
            {
              question: 'Is there a way to tell you what I like - and don’t '
              + 'like - to eat?',
              answer: 'Yep! After you sign-up, answer a few quick questions about '
              + 'your preferences when it comes to cooking and eating so we can '
              + 'suggest meals you’ll love.',
            },
            {
              question: 'When will I receive my order?',
              answer: 'Depending on your shipping location, you’ll receive your '
              + 'weekly delivery on Monday, Tuesday, or Wednesday.',
            },
            {
              question: 'Can I skip a delivery?',
              answer: 'You sure can! You can skip or cancel anytime. Just login '
              + 'and navigate to the “Upcoming Deliveries” page.',
            }]
        }
      />
    </>
  );
}

export default Plans;
